/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable jsx-a11y/accessible-emoji */
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useLocation, useHistory } from 'react-router';
import type { IProps } from './dialer-plans-container';
import PaddleBillingSummaryModal from '../outreach-plans/components/paddle-billing-summary-modal/paddle-billing-summary-modal';
import { previewPaddleSubscription, getUpdatePaymentMethodTransaction } from '../../helpers/billing-subscription.api';
import usePaddleCustomerAuthToken from '../../../../../../shared/hooks/use-paddle-customer-auth-token';
import type { PaddlePreviewSubscriptionResponse } from '../../types/paddle';
import { executeOnRequestStatus } from '../../../../../../shared/utils/execute-on-request-status';
import {
  getIsDialerUserSubscribed,
  getPlanIndex,
  getPlanPeriod,
  getSubscribeAction,
  UpgradePlanQueryParamsKey,
} from './utils/helper';
import PlansSection from './components/plans-section';
import { BillingOption } from '../../../../../../shared/utils/billing-options';
import { getDisabledLabel } from '../modify-subscription/utils/helper';
import { Plan } from '../../types';
import {
  getIsRequestPending,
  redirectWithoutRefresh,
} from '../../../../../../shared/utils';
import { Routes } from '../../../../../../shared/routes';
import PlanCycleSwitch from '../../../../../../shared/design-system/components/molecules/plan-cycle-switch';
import { getDialerPlanMetaData } from './components/dialer-plan-card/utils/helper';
import toaster from '../../../../../../shared/toaster';
import usePaddleIntegration from '../../../../../../shared/hooks/script-integrations/usePaddleIntegration';
import usePaddleCoupon from '../../hooks/use-paddle-coupon';
import UpgradePlanStatic from '../upgrade-plan-static/upgrade-plan-static';
import { supportUrls } from '../../../../../../shared/utils/urls';
import DialerCreditSlider from './components/dialer-credit-slider';
import { PurchaseCreditsType } from '../../types/purchase-credits';
import {
  CreditPurchaseOptions,
  generatePurchaseCreditsPlans,
} from '../../utils/helper';
import type { DialerCreditSliderItem } from './components/dialer-credit-slider/types';

const DialerPlans: React.FC<IProps> = ({
  //* API Request State
  sendGetDialerSubscriptionRequestStatus,
  sendGetDialerSubscriptionResponse,
  sendGetDialerPlansResponse,
  sendGetPurchaseDialerModifySubscriptionRequestStatus,
  resetPurchaseDialerModifySubscriptionResponse,
  //* API Request
  sendPurchaseDialerModifySubscriptionRequest,

  userId,
  shAccountId,
  userEmail,
  paddleCustomerId,
  paddlePolling,
  startPaddlePollingThunk,

  // Credit pricing
  calculateCreditsPricesRequestStatus,
  calculateCreditsPricesResponse,
  purchaseCreditsRequestStatus,
  purchaseCreditsResponse,
  sendCalculateCreditsPricesRequest,
  sendPurchaseCreditsRequest,
  resetCalculateCreditsPricesRequest,
  resetPurchaseCreditsRequest,
}) => {
  const location = useLocation();
  const history = useHistory();

  const [creditSliderValue, setCreditSliderValue] = useState<number[]>([2]);

  //* States
  const [planCycle, setPlanCycle] = useState<BillingOption>(
    BillingOption.ANNUALLY,
  );
  const isPurchaseInProgress = getIsRequestPending(
    sendGetPurchaseDialerModifySubscriptionRequestStatus,
  );
  const [isUserModifyingSubscription, setIsModifySubscription] = useState(
    false,
  );
  const [selectedPlanDetails, setSelectedPlanDetails] = useState<Plan>();

  const [
    isPaddleBillingSummaryModalShow,
    setIsPaddleBillingSummaryModalShow,
  ] = useState(false);
  const [
    paddlePreviewData,
    setPaddlePreviewData,
  ] = useState<PaddlePreviewSubscriptionResponse | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isPaymentMethodUpdate, setIsPaymentMethodUpdate] = useState(false);
  const [isUpdatingPaymentMethod, setIsUpdatingPaymentMethod] = useState(false);

  const { getToken: getCustomerAuthToken } = usePaddleCustomerAuthToken(paddleCustomerId);

  // Credit checkout refs (to avoid popup blocker issues)
  const creditCheckoutOpenRef = useRef(false);
  const creditCheckoutHandledRef = useRef(false);

  const {
    isCouponApplying,
    isCouponApplied,
    isCouponInvalid,
    preAppliedCouponCode,
    onApplyCoupon: onPaddleApplyCoupon,
    onClearCoupon: onPaddleClearCoupon,
    resetCouponState,
  } = usePaddleCoupon({
    planId: selectedPlanDetails?.id,
    paddlePriceId: selectedPlanDetails?.paddlePlanId,
    application: 'dialer',
    shAccountId,
    onPreviewUpdate: setPaddlePreviewData,
  });

  const DIALER_SUBSCRIPTION_URL = '/billing/subscriptions/dialer';

  const isPaddlePolling = paddlePolling?.isActive && paddlePolling?.applicationType === 'dialer';

  const handleCheckoutStateReset = useCallback(() => {
    setIsPaddleBillingSummaryModalShow(false);
    setPaddlePreviewData(null);
    resetCouponState();
  }, [resetCouponState]);

  const { isReady: isPaddleReady, openCheckout } = usePaddleIntegration({
    onCheckoutComplete: (data) => {
      if (isPaymentMethodUpdate) {
        const payment = data?.data?.payment;
        const card = payment?.method_details?.card;
        if (card && paddlePreviewData) {
          setPaddlePreviewData({
            ...paddlePreviewData,
            payload: {
              ...paddlePreviewData.payload,
              cardDetails: [{
                paymentMethodId: payment.payment_method_id || '',
                type: payment.method_details?.type || 'card',
                card: {
                  type: card.type || '',
                  last4: card.last4 || '',
                  expiryMonth: card.expiry_month,
                  expiryYear: card.expiry_year,
                  cardholderName: card.cardholder_name || '',
                },
              }],
            },
          });
        }
        if (window.Paddle?.Checkout?.close) {
          window.Paddle.Checkout.close();
        }
        return;
      }

      // Check if this is a credits checkout completion BEFORE closing checkout
      // to avoid race condition with onCheckoutClosed resetting creditCheckoutOpenRef
      if (creditCheckoutOpenRef.current) {
        creditCheckoutHandledRef.current = true;
        creditCheckoutOpenRef.current = false;

        if (window.Paddle?.Checkout?.close) {
          window.Paddle.Checkout.close();
        }

        const orderId = purchaseCreditsResponse?.transaction?.orderId;
        // Navigate back to dialer subscription info page and trigger polling there
        history.push({
          pathname: DIALER_SUBSCRIPTION_URL,
          state: { creditOrderId: orderId },
        });
        return;
      }

      if (window.Paddle?.Checkout?.close) {
        window.Paddle.Checkout.close();
      }

      handleCheckoutStateReset();
      redirectWithoutRefresh(DIALER_SUBSCRIPTION_URL);
      startPaddlePollingThunk({
        shAccountId,
        applicationType: 'dialer',
        planId: selectedPlanDetails?.id,
      });
    },
    onCheckoutError: () => {
      if (creditCheckoutOpenRef.current) {
        creditCheckoutHandledRef.current = true;
        creditCheckoutOpenRef.current = false;
        resetPurchaseCreditsRequest();
        return;
      }
      toaster.error('Checkout failed. Please try again.');
      handleCheckoutStateReset();
      redirectWithoutRefresh(DIALER_SUBSCRIPTION_URL);
    },
    onError: () => {
      // Paddle integration error - silent fail
    },
    onCheckoutClosed: () => {
      if (isPaymentMethodUpdate) {
        setIsPaymentMethodUpdate(false);
        return;
      }
      if (creditCheckoutOpenRef.current) {
        if (!creditCheckoutHandledRef.current) {
          creditCheckoutOpenRef.current = false;
          resetPurchaseCreditsRequest();
        }
        return;
      }
      handleCheckoutStateReset();
    },
  });

  const handelBillingOption = (value) => setPlanCycle(value);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onSelectPlan = async (planId: number, _planName: string) => {
    if (isPaddlePolling) return;

    const selectedPlan = sendGetDialerPlansResponse.find(
      ({ id }) => id === planId,
    );

    setSelectedPlanDetails(selectedPlan);
    if (
      !isUserModifyingSubscription &&
      selectedPlan?.paddlePlanId &&
      isPaddleReady
    ) {
      const authToken = await getCustomerAuthToken();
      openCheckout({
        items: [{ priceId: selectedPlan.paddlePlanId, quantity: 1 }],
        ...(authToken && { customerAuthToken: authToken }),
        customer: {
          ...(paddleCustomerId
            ? { id: paddleCustomerId }
            : { email: userEmail }),
        },
        customData: {
          user_id: userId,
          sh_account_id: shAccountId,
        },
        settings: {
          displayMode: 'overlay',
          variant: 'one-page',
          theme: 'light',
          ...(paddleCustomerId && { allowLogout: false }),
        },
      });
      return;
    }

    if (isUserModifyingSubscription) {
      setIsPreviewLoading(true);
      setIsPaddleBillingSummaryModalShow(true);
      try {
        const response = await previewPaddleSubscription({
          accountSubscriptionPlanId: planId,
          application: 'dialer',
          shAccountId,
        });
        setPaddlePreviewData(response);
      } catch {
        toaster.error('Failed to load billing preview. Please try again.');
        setIsPaddleBillingSummaryModalShow(false);
      } finally {
        setIsPreviewLoading(false);
      }
      updateQueryParams(planId);
    }
  };

  const onPaddlePayNow = () => {
    sendPurchaseDialerModifySubscriptionRequest({
      accountSubscriptionPlanId: Number(selectedPlanDetails?.id),
      updateType: 'base_only',
      ...(preAppliedCouponCode && { couponCode: preAppliedCouponCode }),
    });
  };

  const onUpdateCardDetails = async () => {
    const { subscriptionId } = sendGetDialerSubscriptionResponse;
    if (!subscriptionId) return;

    const authToken = await getCustomerAuthToken();
    try {
      setIsUpdatingPaymentMethod(true);
      const response = await getUpdatePaymentMethodTransaction(subscriptionId);
      const transactionId = (response as any)?.payload?.transactionId;
      if (!transactionId) return;

      setIsPaymentMethodUpdate(true);
      openCheckout({
        transactionId,
        ...(authToken && { customerAuthToken: authToken }),
        settings: { displayMode: 'overlay', theme: 'light', variant: 'one-page' },
      });
    } catch {
      toaster.error('Failed to update payment method. Please try again.');
    } finally {
      setIsUpdatingPaymentMethod(false);
    }
  };

  const hidePaddleBillingSummaryModal = () => {
    setIsPaddleBillingSummaryModalShow(false);
    setPaddlePreviewData(null);
    resetQueryParams();
    resetCouponState();
  };

  const updateQueryParams = (planId) => {
    const queryParams = new URLSearchParams(location.search);
    const index = getPlanIndex(sendGetDialerPlansResponse, planId);
    const selectPlanIndex = index < 3 ? index + 1 : index - 2;

    queryParams.set(
      UpgradePlanQueryParamsKey.Action,
      getSubscribeAction(isUserModifyingSubscription),
    );
    queryParams.set(UpgradePlanQueryParamsKey.Period, getPlanPeriod(planCycle));
    queryParams.set(UpgradePlanQueryParamsKey.Plan, selectPlanIndex.toString());

    history.push({
      pathname: Routes.Setting_Billing_Upgrade_Dialer_Plan,
      search: queryParams.toString(),
    });
  };

  const resetQueryParams = () => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.delete(UpgradePlanQueryParamsKey.Action);
    queryParams.delete(UpgradePlanQueryParamsKey.Period);
    queryParams.delete(UpgradePlanQueryParamsKey.Plan);

    history.push({
      pathname: Routes.Setting_Billing_Upgrade_Dialer_Plan,
      search: queryParams.toString(),
    });
  };

  const getActivePlanName = () => {
    const { planName } = sendGetDialerSubscriptionResponse || {};
    const dialerPlanMetaData = getDialerPlanMetaData(planName);

    return dialerPlanMetaData?.displayName || planName;
  };

  // ── Credit slider data ─────────────────────────────────────────────────

  const isCreditPricesLoading = getIsRequestPending(
    calculateCreditsPricesRequestStatus,
  );

  const creditSliderItems: DialerCreditSliderItem[] = useMemo(() => {
    if (!calculateCreditsPricesResponse) return [];
    const plans = generatePurchaseCreditsPlans(
      calculateCreditsPricesResponse,
      PurchaseCreditsType.DC_CREDITS,
    );
    return plans.map((plan) => ({
      label: plan.name,
      credits: plan.credits,
      price: plan.amount,
    }));
  }, [calculateCreditsPricesResponse]);

  const selectedCreditPlan = useMemo(() => {
    if (creditSliderItems.length > 0) {
      return creditSliderItems[creditSliderValue[0]] || null;
    }
    return null;
  }, [creditSliderValue, creditSliderItems]);

  const handleBuyNowCredits = () => {
    if (!selectedCreditPlan || isPaddlePolling) return;

    sendPurchaseCreditsRequest({
      creditCount: selectedCreditPlan.credits,
      type: PurchaseCreditsType.DC_CREDITS,
      currencyCode: 'USD',
      paymentProvider: 'paddle',
    });
  };

  // ── useEffects ─────────────────────────────────────────────────────────

  // Fetch credit prices on mount
  useEffect(() => {
    sendCalculateCreditsPricesRequest({
      creditCounts: CreditPurchaseOptions[PurchaseCreditsType.DC_CREDITS],
      type: PurchaseCreditsType.DC_CREDITS,
      currencyCode: 'USD',
    });

    return () => {
      resetCalculateCreditsPricesRequest();
      resetPurchaseCreditsRequest();
    };
  }, []);

  //* useEffect
  useEffect(() => {
    executeOnRequestStatus({
      status: sendGetDialerSubscriptionRequestStatus,
      onSuccess: () => {
        const { planCode } = sendGetDialerSubscriptionResponse;

        const isUserSubscribed = getIsDialerUserSubscribed(planCode);
        setIsModifySubscription(isUserSubscribed);
      },
    });
  }, [sendGetDialerSubscriptionRequestStatus]);

  // Modify subscription
  useEffect(() => {
    executeOnRequestStatus({
      status: sendGetPurchaseDialerModifySubscriptionRequestStatus,
      onSuccess: () => {
        hidePaddleBillingSummaryModal();
        redirectWithoutRefresh(DIALER_SUBSCRIPTION_URL);
        startPaddlePollingThunk({
          shAccountId,
          applicationType: 'dialer',
          planId: selectedPlanDetails?.id,
        });
        resetPurchaseDialerModifySubscriptionResponse();
      },
    });
  }, [sendGetPurchaseDialerModifySubscriptionRequestStatus]);

  // Handle Paddle checkout opening when credits purchase request succeeds
  useEffect(() => {
    executeOnRequestStatus({
      status: purchaseCreditsRequestStatus,
      onSuccess: async () => {
        if (creditCheckoutOpenRef.current) return;

        if (!isPaddleReady) {
          resetPurchaseCreditsRequest();
          return;
        }

        creditCheckoutOpenRef.current = true;
        creditCheckoutHandledRef.current = false;

        const authToken = await getCustomerAuthToken();
        openCheckout({
          transactionId: purchaseCreditsResponse?.transaction?.transactionId,
          ...(!paddleCustomerId && { customer: { email: userEmail } }),
          ...(authToken && { customerAuthToken: authToken }),
          settings: {
            displayMode: 'overlay',
            variant: 'one-page',
            theme: 'light',
            ...(paddleCustomerId && { allowLogout: false }),
          },
        });
      },
      onFailed: () => {
        resetPurchaseCreditsRequest();
      },
    });
  }, [purchaseCreditsRequestStatus]);

  return (
    <div className="dialer-plans">
      <div className="dialer-plans-header">
        <h1>Find the right Dialer Plan</h1>
        <span>
          You are currently on the{' '}
          <span className="semibold-1">{getActivePlanName()} plan</span>
        </span>
      </div>

      <div
        className="d-flex flex-column justify-content-center align-items-center"
        style={{ background: '#f9fafb', height: '43px', paddingTop: '18px' }}
      >
        <div className="d-flex flex-column align-items-end mt-4">
          <PlanCycleSwitch
            value={planCycle}
            onChange={handelBillingOption}
            disabledLabel={getDisabledLabel(sendGetDialerSubscriptionResponse)}
          />
        </div>
      </div>

      <div className="dialer-plans-section">
        <PlansSection
          onSelectPlan={onSelectPlan}
          planCycle={planCycle}
          selectedPlanId={selectedPlanDetails?.id}
          isPaddlePolling={isPaddlePolling}
        />
        <DialerCreditSlider
          creditSliderItems={creditSliderItems}
          creditSliderValue={creditSliderValue}
          setCreditSliderValue={setCreditSliderValue}
          isLoading={isCreditPricesLoading}
          onBuyNow={handleBuyNowCredits}
          isBuyNowLoading={getIsRequestPending(purchaseCreditsRequestStatus)}
          isBuyNowDisabled={
            !isPaddleReady || isPaddlePolling || !selectedCreditPlan
          }
        />
      </div>

      <div className="dialer-secondary-banner-container">
        <UpgradePlanStatic link={supportUrls.dialerPlanViewFAQ} />
      </div>

      <PaddleBillingSummaryModal
        show={isPaddleBillingSummaryModalShow}
        onClose={hidePaddleBillingSummaryModal}
        planName={selectedPlanDetails?.planName || ''}
        previewData={paddlePreviewData}
        onPayNow={onPaddlePayNow}
        isPurchaseInProgress={isPurchaseInProgress}
        isPreviewLoading={isPreviewLoading}
        onUpdateCardDetails={onUpdateCardDetails}
        isUpdatingPaymentMethod={isUpdatingPaymentMethod}
        onApplyCoupon={onPaddleApplyCoupon}
        onClearCoupon={onPaddleClearCoupon}
        isCouponApplying={isCouponApplying}
        isCouponApplied={isCouponApplied}
        isCouponInvalid={isCouponInvalid}
      />
    </div>
  );
};

export default DialerPlans;
